import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from "react"
import { useNavigate } from "react-router-dom"
import { useAppSelector } from "@/store/store"
import { useCartStore } from "@/store/cart/cart-store"
import { useCartPersistCrossTabSync } from "@/store/cart/use-cart-persist-cross-tab-sync"
import ActiveUnpaidOrderSheet from "@/pages/order-checkout/active-unpaid-order-sheet"
import type { ActiveUnpaidOrderConflict } from "@/domain/order/active-unpaid-order"
import { getOwnedPaymentSession, getPaymentSession } from "@/domain/payment/payment-await-session"
import { storefrontJoin, useStorefrontBasePath } from "@/utils/storefront-paths"

const CART_LOCKED_TITLE = "Cart temporarily locked"
const CART_LOCKED_MESSAGE =
  "You already have an order awaiting payment. Complete or cancel that order before adding items to a new order."
const CART_LOCKED_FALLBACK_MESSAGE =
  "You already have an order awaiting payment. Please open My Orders to continue or cancel that order before starting a new order."

type CartLockRecoveryContextValue = {
  /**
   * Call when a cart mutation is blocked by the existing cart lock.
   * Opens at most one recovery sheet; no-op when the cart is not locked.
   */
  notifyCartMutationBlocked: () => void
}

const CartLockRecoveryContext = createContext<CartLockRecoveryContextValue | null>(null)

function resolveBlockingConflict(
  activeUnpaid: ActiveUnpaidOrderConflict | null,
  awaitingPaymentOrderId: string | null,
  pendingOrderId: string | null,
  lastFinalBillingOrderId: string | null | undefined,
  sessionOrderId?: string | null
): ActiveUnpaidOrderConflict | null {
  if (activeUnpaid?.orderId) return activeUnpaid

  const orderId =
    String(awaitingPaymentOrderId ?? "").trim() ||
    String(pendingOrderId ?? "").trim() ||
    String(lastFinalBillingOrderId ?? "").trim() ||
    String(sessionOrderId ?? "").trim()
  if (!orderId) return null

  // Minimal conflict shape for navigation — same sheet as checkout 409 UX.
  return {
    orderId,
    orderNumber: null,
    billingSnapshotId: null,
    payableAmount: null,
    currencyCode: null,
    paymentReady: false,
    paymentStatus: null,
    createdDate: null,
    receivedAt: new Date().toISOString(),
  }
}

/**
 * Surfaces recovery UX when Add/quantity is blocked by the existing cart lock
 * (set during an in-flight unpaid prepaid order). Does not poll or invent lock rules.
 */
export function CartLockRecoveryProvider({
  children,
}: {
  children: ReactNode
}): ReactElement {
  const navigate = useNavigate()
  const basePath = useStorefrontBasePath()
  const [open, setOpen] = useState(false)
  /** True when locked but no orderId could be resolved — generic recovery, no fabricated id. */
  const [fallbackWithoutOrderId, setFallbackWithoutOrderId] = useState(false)

  useCartPersistCrossTabSync()

  const activeUnpaidOrder = useAppSelector((s) => s.checkout.activeUnpaidOrder)
  const awaitingPaymentOrderId = useAppSelector((s) => s.finalBilling.awaitingPaymentOrderId)
  const pendingOrderId = useAppSelector((s) => s.finalBilling.pendingOrderId)
  const lastFinalBillingOrderId = useAppSelector(
    (s) => s.finalBilling.lastFinalBilling?.order_id ?? null
  )

  const ownedSession = getOwnedPaymentSession(basePath)
  const foreignSessionOrderId =
    !ownedSession && getPaymentSession() ? getPaymentSession()!.orderId : null
  const sessionOrderId = ownedSession?.orderId ?? null

  const conflict = useMemo(
    () =>
      resolveBlockingConflict(
        foreignSessionOrderId && activeUnpaidOrder?.orderId === foreignSessionOrderId
          ? null
          : activeUnpaidOrder,
        foreignSessionOrderId && awaitingPaymentOrderId === foreignSessionOrderId
          ? null
          : awaitingPaymentOrderId,
        foreignSessionOrderId && pendingOrderId === foreignSessionOrderId
          ? null
          : pendingOrderId,
        foreignSessionOrderId && lastFinalBillingOrderId === foreignSessionOrderId
          ? null
          : lastFinalBillingOrderId,
        sessionOrderId
      ),
    [
      activeUnpaidOrder,
      awaitingPaymentOrderId,
      pendingOrderId,
      lastFinalBillingOrderId,
      sessionOrderId,
      foreignSessionOrderId,
    ]
  )

  const notifyCartMutationBlocked = useCallback(() => {
    const { isLocked, isCheckoutLocked } = useCartStore.getState()
    // Existing cart-lock condition only — no new business rule.
    if (!isLocked && !isCheckoutLocked) return
    const owned = getOwnedPaymentSession(basePath)
    const foreignOrderId =
      !owned && getPaymentSession() ? getPaymentSession()!.orderId : null
    const next = resolveBlockingConflict(
      foreignOrderId && activeUnpaidOrder?.orderId === foreignOrderId
        ? null
        : activeUnpaidOrder,
      foreignOrderId && awaitingPaymentOrderId === foreignOrderId
        ? null
        : awaitingPaymentOrderId,
      foreignOrderId && pendingOrderId === foreignOrderId ? null : pendingOrderId,
      foreignOrderId && lastFinalBillingOrderId === foreignOrderId
        ? null
        : lastFinalBillingOrderId,
      owned?.orderId ?? null
    )
    if (next?.orderId) {
      setFallbackWithoutOrderId(false)
      setOpen(true)
      return
    }
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console -- dev-only diagnostic for unresolved lock recovery
      console.warn(
        "[CartLockRecovery] Cart locked without resolvable orderId; showing fallback recovery sheet."
      )
    }
    setFallbackWithoutOrderId(true)
    setOpen(true)
  }, [
    activeUnpaidOrder,
    awaitingPaymentOrderId,
    pendingOrderId,
    lastFinalBillingOrderId,
    basePath,
  ])

  const handleClose = useCallback(() => {
    setOpen(false)
  }, [])

  const handleContinuePayment = useCallback(() => {
    const orderId = conflict?.orderId
    if (!orderId) return
    const owned = getOwnedPaymentSession(basePath)
    const raw = getPaymentSession()
    if (raw && !owned && raw.orderId === orderId) return
    setOpen(false)
    navigate(
      storefrontJoin(basePath, `/checkout/recover?orderId=${encodeURIComponent(orderId)}`)
    )
  }, [basePath, conflict?.orderId, navigate])

  const handleViewOrder = useCallback(() => {
    if (fallbackWithoutOrderId || !conflict?.orderId) {
      setOpen(false)
      navigate(storefrontJoin(basePath, "/my-account/orders"))
      return
    }
    setOpen(false)
    navigate(
      storefrontJoin(basePath, `/my-account/orders/${encodeURIComponent(conflict.orderId)}`)
    )
  }, [basePath, conflict?.orderId, fallbackWithoutOrderId, navigate])

  const value = useMemo(
    () => ({ notifyCartMutationBlocked }),
    [notifyCartMutationBlocked]
  )

  const sheetMessage = fallbackWithoutOrderId
    ? CART_LOCKED_FALLBACK_MESSAGE
    : CART_LOCKED_MESSAGE

  return (
    <CartLockRecoveryContext.Provider value={value}>
      {children}
      <ActiveUnpaidOrderSheet
        open={open}
        conflict={fallbackWithoutOrderId ? null : conflict}
        title={CART_LOCKED_TITLE}
        message={sheetMessage}
        hideContinuePayment={fallbackWithoutOrderId}
        showViewOrder
        viewOrderLabel={fallbackWithoutOrderId ? "View orders" : "View order"}
        onContinuePayment={handleContinuePayment}
        onViewOrder={handleViewOrder}
        onClose={handleClose}
      />
    </CartLockRecoveryContext.Provider>
  )
}

export function useCartLockRecovery(): CartLockRecoveryContextValue {
  const ctx = useContext(CartLockRecoveryContext)
  if (!ctx) {
    return {
      notifyCartMutationBlocked: () => {
        /* Provider optional in isolated tests */
      },
    }
  }
  return ctx
}
